import { useEffect, useMemo, useRef, useState } from 'react';

import { motion } from 'framer-motion';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { FixedSizeList as List } from 'react-window';
import { toast } from 'sonner';

import { DocRelationships } from './doc-relationships';
import { DocViewerFullFact } from './doc-viewer-full-fact';
import { DocViewerFactSidebar } from './doc-viewer-sidebar';
import { SubsetFact } from '../../../../../backend/src/types/types';
import useGetSubsetFacts from '../../../api/queries/useGetSubsetFacts';
import { ToastDrawer } from '../drawers/toast-drawer';
import { useGetDocV2 } from '@/api/queries/useGetDocV2';
import { Icon } from '@/new/components/icon';
import { MetadataDisplay, buildFieldsArray } from '@/new/components/metadata-display';
import { PDFViewer, PDFViewerRef } from '@/new/components/pdf-viewer';
import { Button } from '@/new/components/shadcn/button';
import { Popover, PopoverTrigger, PopoverContent } from '@/new/components/shadcn/popover';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/new/components/shadcn/tooltip';
import { processFactHighlights, sortFactsByAppearance } from '@/new/lib/doc-viewer-utils';

export function LargeDocViewer({ subsetId, docId }: { subsetId: string; docId: string }) {
  // State
  const listRef = useRef<List>(null);
  const pdfViewerRef = useRef<PDFViewerRef>(null);
  const location = useLocation();
  const navigate = useNavigate();

  // Get initial searchQuery from URL params
  const searchParams = new URLSearchParams(location.search);
  const initialSearchQuery = searchParams.get('searchQuery');

  const [highlightedFactId, setHighlightedFactId] = useState<string | null>(null);
  const [selectedFact, setSelectedFact] = useState<SubsetFact | null>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isSidebarVisible, setIsSidebarVisible] = useState(true);

  const { matterId } = useParams();
  if (!matterId || !subsetId || !docId) return null;

  // Data fetching -------------
  const { data: factsData, isLoading } = useGetSubsetFacts({
    matterId,
    subsetId,
    filters: { docIds: [{ label: docId, value: docId }] },
    paginated: false,
  });

  const facts = factsData?.facts;

  // Fetch doc with parent/child files and surrounding doc ids
  const { data: doc } = useGetDocV2(matterId, docId, subsetId, true);
  const docName = doc?.file?.name;

  // Coordinate and fact processing
  const { sortedFacts, coordinates, highlightFactMap } = useMemo(() => {
    if (!facts) return { sortedFacts: [], coordinates: [] };
    const sortedFacts = sortFactsByAppearance(facts, docId);
    const { coordinates, highlightFactMap } = processFactHighlights(sortedFacts, docId);

    return { sortedFacts, coordinates, highlightFactMap };
  }, [factsData, docId]);

  const toggleDrawer = () => {
    setIsDrawerOpen(!isDrawerOpen);
    setSelectedFact(null);
  };

  const selectFact = (fact: SubsetFact) => {
    // Clear search when selecting a fact
    pdfViewerRef.current?.clearSearch();
    setSelectedFact(fact);
    setIsDrawerOpen(true);
  };

  const scrollToFact = (id: string) => {
    // Clear search when clicking on a fact highlight
    pdfViewerRef.current?.clearSearch();

    const index = highlightFactMap?.get(id);
    if (index !== undefined && listRef.current) {
      setHighlightedFactId(id);
      listRef.current.scrollToItem(index, 'center');

      // Find the fact in the sortedFacts array
      const fact = sortedFacts.find((fact) => fact.id === id);
      if (fact) {
        const location = fact.locations.find((location) => location.docId === docId);
        if (location) {
          navigator.clipboard.writeText(location?.sources?.map((source) => source.text).join('\n') ?? '');
          toast.success('Source text copied to clipboard');
        }
      }
    }
  };

  const scrollToHighlight = (id: string) => {
    // Clear search when clicking magnifying glass
    pdfViewerRef.current?.clearSearch();
    setHighlightedFactId(id);
  };

  // Check for factId and searchQuery in URL params, then remove them after use
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const factIdFromUrl = searchParams.get('factId');
    const hasSearchQuery = searchParams.has('searchQuery');
    let shouldUpdateUrl = false;

    if (factIdFromUrl && highlightFactMap && highlightFactMap.has(factIdFromUrl)) {
      // Scroll to the fact
      scrollToHighlight(factIdFromUrl);
      searchParams.delete('factId');
      shouldUpdateUrl = true;
    }

    // Remove searchQuery from URL after initial load
    if (hasSearchQuery) {
      searchParams.delete('searchQuery');
      shouldUpdateUrl = true;
    }

    if (shouldUpdateUrl) {
      const newUrl = `${location.pathname}?${searchParams.toString()}`;
      navigate(newUrl, { replace: true });
    }
  }, [highlightFactMap, location, navigate]);

  return (
    <div className="flex h-full">
      <div className={`overflow-hidden transition-all duration-300 ${isSidebarVisible ? 'w-7/12' : 'w-full'}`}>
        <div className={`flex h-14 items-center justify-between border-b ${isSidebarVisible ? 'pr-2' : ''}`}>
          <div className="grid w-full grid-cols-[min-content_1fr] items-center gap-4 pr-2">
            <Button
              className="flex items-center rounded-full border bg-gray-100 px-2 py-0.5 text-sm whitespace-nowrap hover:bg-gray-200"
              onClick={() => navigate(`/matters/${matterId}/subsets/${subsetId}/docs`)}
            >
              <Icon className="lucide--arrow-left" />
              All Docs
            </Button>
            <div className="flex w-full items-center justify-between gap-2">
              <p className="line-clamp-1 max-w-11/12 items-center break-all text-ellipsis">{docName}</p>

              <div className="flex items-center gap-2">
                {doc?.file.fileMetadata && (() => {
                  const metadata = doc.file.fileMetadata;
                  const hasRelativityFields =
                    metadata.emailSentDate ||
                    metadata.documentDate ||
                    buildFieldsArray(metadata.relativityFields ?? {}).length > 0;

                  return (
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className={`flex items-center gap-1.5 rounded-full border-gray-300 text-xs hover:border-gray-400 ${
                            hasRelativityFields
                              ? 'bg-purple-50 text-purple-700 hover:bg-purple-100 hover:text-purple-700'
                              : 'bg-gray-50 text-gray-700 hover:bg-gray-100 hover:text-gray-700'
                          }`}
                        >
                          <Icon
                            className={`h-3 w-3 ${
                              hasRelativityFields ? 'lucide--database text-purple-700' : 'lucide--sparkles text-gray-700'
                            }`}
                          />
                          Metadata
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="max-h-[500px] w-auto max-w-none overflow-y-auto" align="start">
                        <MetadataDisplay metadata={metadata} compact includeWexlerFields />
                      </PopoverContent>
                    </Popover>
                  );
                })()}
                {((doc?.file.parentFiles && doc?.file.parentFiles.length > 0) ||
                  (doc?.file?.childFiles && doc?.file?.childFiles?.length > 0)) && (
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button className="rounded-full border text-xs">Attachments</Button>
                    </PopoverTrigger>
                    <PopoverContent>
                      <DocRelationships doc={doc} />
                    </PopoverContent>
                  </Popover>
                )}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1">
              <Button
                size="iconSm"
                disabled={doc?.prevDocId === undefined}
                onClick={() => navigate(`/matters/${matterId}/subsets/${subsetId}/docs/${doc?.prevDocId}`)}
              >
                <Icon className="lucide--arrow-left" />
              </Button>

              <Button
                size="iconSm"
                disabled={doc?.nextDocId === undefined}
                onClick={() => navigate(`/matters/${matterId}/subsets/${subsetId}/docs/${doc?.nextDocId}`)}
              >
                <Icon className="lucide--arrow-right" />
              </Button>
            </div>

            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  onClick={() => setIsSidebarVisible(!isSidebarVisible)}
                  aria-label={isSidebarVisible ? 'Hide facts sidebar' : 'Show facts sidebar'}
                  variant="ghost"
                >
                  {isSidebarVisible ? (
                    <Icon className="lucide--panel-right-close" />
                  ) : (
                    <Icon className="lucide--panel-right-open" />
                  )}
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>{isSidebarVisible ? 'Hide facts' : 'Show facts'}</p>
              </TooltipContent>
            </Tooltip>
          </div>
        </div>
        <div className="relative h-[calc(100%-4rem)]">
          <PDFViewer
            ref={pdfViewerRef}
            highlightedFactId={highlightedFactId}
            setHighlightedFactId={setHighlightedFactId}
            highlights={coordinates}
            onHighlightClick={scrollToFact}
            docId={docId}
            hideFeatureButtons={true}
            fileName={docName}
            initialSearchQuery={initialSearchQuery ?? undefined}
          />
        </div>
      </div>

      {isSidebarVisible && (
        <DocViewerFactSidebar
          docId={docId}
          isLoading={isLoading}
          factTotal={factsData?.totalCount ?? null}
          jumpToHighlightArea={scrollToHighlight}
          highlightedFactId={highlightedFactId}
          selectFact={selectFact}
          facts={sortedFacts ?? []}
          listRef={listRef}
        />
      )}

      <ToastDrawer isOpen={isDrawerOpen} toggleDrawer={toggleDrawer} position="right">
        {selectedFact && (
          <motion.div key={selectedFact.id}>
            <DocViewerFullFact fact={selectedFact} docId={docId} subsetId={subsetId} showDocumentDetails />
          </motion.div>
        )}
      </ToastDrawer>
    </div>
  );
}
